import {
  all,
  takeLatest,
  takeEvery,
  put,
  call,
  select,
} from 'redux-saga/effects';
import {
  createCustomObject,
  updateCustomObject,
  fetchCustomObjects,
  fetchCustomObjectById,
  pinCustomObject,
  archiveCustomObject,
  createCustomObjectInstance,
  updateCustomObjectInstance,
  archiveCustomObjectInstance,
  fetchCustomObjectInstances,
  restoreCustomObjectInstance,
  fetchCustomObjectsLimit,
} from '../routines/customObjectRoutines';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';
import customObjectsApi from '../../api/customObjects';
import { CUSTOM_OBJECT_BASE_PATH } from '../../common/constants/CustomObjectConstants';

// =============Custom Object Sagas=============

function* fetchCustomObjectsLimitSaga() {
  try {
    const url = `${envConfig.APIG_URL}core/configs/CUSTOM_OBJECT`;
    const { data } = yield call(request, url);
    const customObjectLimit =
      data.find((item) => item.key === 'CUSTOM_OBJECT_LIMIT')?.value || null;
    yield put(fetchCustomObjectsLimit.success(customObjectLimit));
  } catch (error) {
    yield put(fetchCustomObjectsLimit.failure({ error }));
  }
}

function* createCustomObjectSaga({ payload }) {
  try {
    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}`;
    const response = yield call(request, url, {
      method: 'POST',
      payload,
    });
    yield put(
      createCustomObject.success({
        response,
        successMessage: `Custom Object ${payload.pluralObjectName} successfully created`,
      })
    );
    yield put(fetchCustomObjects.trigger());
  } catch (error) {
    yield put(createCustomObject.failure({ error }));
  }
}

function* updateCustomObjectSaga({ payload }) {
  try {
    const { id, formData } = payload;

    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/${id}`;
    const response = yield call(request, url, {
      method: 'PUT',
      payload: formData,
    });
    yield put(
      updateCustomObject.success({
        response,
        successMessage: `Custom Object ${formData.pluralObjectName} successfully edited`,
      })
    );
    yield put(fetchCustomObjects.trigger());
  } catch (error) {
    yield put(updateCustomObject.failure({ error }));
  }
}

function* fetchCustomObjectsSaga({ payload }) {
  const idsPath = payload?.ids?.length > 0 ? `/ids?${payload?.ids}` : '';
  try {
    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}${idsPath}`;
    const response = yield call(request, url);
    yield put(fetchCustomObjects.success(response.data));
  } catch (error) {
    yield put(fetchCustomObjects.failure({ error }));
  }
}

function* fetchCustomObjectByIdSaga({ payload }) {
  const { id } = payload;
  try {
    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/${id}`;
    const response = yield call(request, url);
    yield put(fetchCustomObjectById.success(response.data[0]));
  } catch (error) {
    yield put(fetchCustomObjectById.failure({ error }));
  }
}

export async function validateCOValuesExist({
  apiName,
  singularName,
  pluralName,
  externalId,
}) {
  let URL = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}`;
  if (singularName) {
    URL = `${URL}?singularName=${singularName}`;
  } else if (pluralName) {
    URL = `${URL}?pluralName=${pluralName}`;
  } else if (apiName) {
    URL = `${URL}?apiName=${apiName}`;
  } else if (externalId) {
    URL = `${URL}?externalId=${externalId}`;
  } else {
    throw new Error('No Value Entered!');
  }

  try {
    const response = await request(URL);
    return response;
  } catch (error) {
    return null;
  }
}

export function* pinCustomObjectSaga({ payload }) {
  const { customObjectId, action } = payload;
  try {
    const URL = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/${customObjectId}/pin`;
    const config = {
      method: 'POST',
      payload: { action },
    };
    const response = yield call(request, URL, config);
    yield put(pinCustomObject.success(response.data));
    yield put(fetchCustomObjects.trigger());
  } catch (error) {
    yield put(pinCustomObject.failure({ error }));
  } finally {
    yield put(pinCustomObject.fulfill());
  }
}

function* archiveCustomObjectSaga({ payload }) {
  try {
    const { customObjectId, pluralObjectName } = payload;

    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/${customObjectId}`;
    const response = yield call(request, url, {
      method: 'PUT',
      payload: {},
    });
    yield put(
      archiveCustomObject.success({
        response,
        successMessage: `Custom Object ${pluralObjectName} successfully archived`,
      })
    );
  } catch (error) {
    yield put(archiveCustomObject.failure({ error }));
  }
}

// =============Custom Object Instance Sagas=============

function* createCustomObjectInstanceSaga({ payload }) {
  try {
    const { headers, formData } = payload;
    const { apiName, entityName } = headers;

    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/instance/${apiName}`;
    const response = yield call(request, url, {
      method: 'POST',
      payload: formData,
    });
    yield put(
      createCustomObjectInstance.success({
        response,
        successMessage: `${entityName} created successfully`,
      })
    );
    yield put(
      fetchCustomObjectInstances.trigger({
        usePrevPayload: true,
        apiName,
      })
    );
  } catch (error) {
    yield put(createCustomObjectInstance.failure({ error }));
  }
}

function* updateCustomObjectInstanceSaga({ payload }) {
  try {
    const { headers, formData } = payload;
    const { apiName, instanceId, entityName } = headers;

    const _formData = {
      ...formData,
      externalId: formData.externalId === '' ? null : formData.externalId,
    };

    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/instance/${apiName}/${instanceId}`;

    const response = yield call(request, url, {
      method: 'PUT',
      payload: _formData,
    });
    yield put(
      updateCustomObjectInstance.success({
        response,
        successMessage: `${entityName} updated successfully`,
      })
    );
    yield put(
      fetchCustomObjectInstances.trigger({
        usePrevPayload: true,
        preservePreviousList: true,
        apiName,
      })
    );
  } catch (error) {
    yield put(updateCustomObjectInstance.failure({ error }));
  }
}

function* archiveCustomObjectInstanceSaga({ payload }) {
  try {
    const { headers } = payload;
    const { apiName, instanceId, entityName } = headers;

    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/instance/${apiName}/${instanceId}/archive`;
    const response = yield call(request, url, {
      method: 'PUT',
      payload: {},
    });
    yield put(
      archiveCustomObjectInstance.success({
        response,
        successMessage: `${entityName} archived successfully`,
      })
    );
    yield put(
      fetchCustomObjectInstances.trigger({
        usePrevPayload: true,
        apiName,
      })
    );
  } catch (error) {
    yield put(archiveCustomObjectInstance.failure({ error }));
  }
}

function* restoreCustomObjectInstanceSaga({ payload }) {
  try {
    const { headers } = payload;
    const { apiName, instanceId, entityName } = headers;

    const url = `${envConfig.APIG_URL}${CUSTOM_OBJECT_BASE_PATH}/instance/${apiName}/${instanceId}/restore`;
    const response = yield call(request, url, {
      method: 'PUT',
      payload: {},
    });
    yield put(
      restoreCustomObjectInstance.success({
        response,
        successMessage: `${entityName} restored successfully`,
      })
    );
    yield put(
      fetchCustomObjectInstances.trigger({
        usePrevPayload: true,
        apiName,
      })
    );
  } catch (error) {
    yield put(restoreCustomObjectInstance.failure({ error }));
  }
}

function* fetchCustomObjectInstancesSaga({ payload }) {
  const { apiName } = payload;

  try {
    const displayCurrency = yield select(
      (state) => state.currentUser.user.displayCurrency
    );
    const prevPayload = yield select(
      (state) => state.customObject.currentPayload[apiName]
    );

    let payloadToUse;

    if (payload && payload.usePrevPayload) {
      payloadToUse = { ...prevPayload };
    } else {
      payloadToUse = { ...payload };
    }
    if (payload && payload.entityIds) {
      // always override
      payloadToUse = payloadToUse
        ? { ...payloadToUse, entityIds: payload.entityIds }
        : { entityIds: payload.entityIds };
    }
    if (payload && payload.forcePage) {
      // always override
      payloadToUse = payloadToUse
        ? { ...payloadToUse, page: payload.forcePage }
        : { page: payload.forcePage };
    }

    const response = yield call(
      customObjectsApi.getCustomObjectInstances,
      displayCurrency,
      payloadToUse,
      apiName
    );
    yield put(
      fetchCustomObjectInstances.success({
        ...response,
        currentPayload: payloadToUse,
      })
    );
  } catch (error) {
    yield put(fetchCustomObjectInstances.failure({ error }));
  } finally {
    yield put(fetchCustomObjectInstances.fulfill());
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchCustomObjectsLimit.TRIGGER, fetchCustomObjectsLimitSaga),
    takeEvery(createCustomObject.TRIGGER, createCustomObjectSaga),
    takeEvery(updateCustomObject.TRIGGER, updateCustomObjectSaga),
    takeLatest(fetchCustomObjects.TRIGGER, fetchCustomObjectsSaga),
    takeLatest(fetchCustomObjectById.TRIGGER, fetchCustomObjectByIdSaga),
    takeEvery(pinCustomObject.TRIGGER, pinCustomObjectSaga),
    takeEvery(archiveCustomObject.TRIGGER, archiveCustomObjectSaga),
    takeEvery(
      restoreCustomObjectInstance.TRIGGER,
      restoreCustomObjectInstanceSaga
    ),
    takeEvery(
      createCustomObjectInstance.TRIGGER,
      createCustomObjectInstanceSaga
    ),
    takeEvery(
      updateCustomObjectInstance.TRIGGER,
      updateCustomObjectInstanceSaga
    ),
    takeEvery(
      archiveCustomObjectInstance.TRIGGER,
      archiveCustomObjectInstanceSaga
    ),
    takeLatest(
      fetchCustomObjectInstances.TRIGGER,
      fetchCustomObjectInstancesSaga
    ),
  ]);
}
